/**
 * The document head and status in React Router framework mode (web plan W4.3).
 *
 * The W1.3 decisions stay where they are (server/routes.mjs: the route table,
 * the resolver, the cache, the redirect and status rules). The server hands
 * the root route one bound `resolve()` through the load context
 * (server/framework.mjs); `loadDocument` turns its answer into React Router's
 * terms, and `headMeta` / `jsonLdHtml` render the same tags server/head.mjs
 * injected into `dist/index.html`:
 *
 * - 301 / 302 → a thrown `redirect` (Location kept exactly, query included);
 * - 200 / 404 / 410 → the head as loader data with that status, so the page
 *   renders as before (`<App/>`'s own not-found state on a 404) and the
 *   response carries the status;
 * - 503 (backend down, nothing cached) → a thrown `data(null, 503)` with
 *   `Retry-After`: the root ErrorBoundary answers, `noindex`;
 * - no load context (the `react-router dev` server, tests) → no head: the
 *   page renders its own (@/components/seo/PageHead, web plan W5.1), as it
 *   does after every in-app navigation (`servesDocumentHead`).
 *
 * Cache-Control is W1.3's (`documentCacheControl`, kept equal to
 * server/routes.mjs's `cacheControlFor` by server/framework.test.mjs).
 *
 * Pure module: no `window`, no React.
 */
import {
  data,
  redirect,
  type AppLoadContext,
  type MetaDescriptor,
  type UNSAFE_DataWithResponseInit as DataWithResponseInit,
} from 'react-router';
import { tFor, type Translate } from '@/i18n';
import { SERVER_HEAD_ATTRIBUTE } from '@/lib/serverHead';
import { LEGACY_ROUTES, movedPathFor } from '@/routes/legacyRoutes';
import type { RoutePreloadData } from '@/seo/routePreload';
import { brandsListHead, homeHead, ogImageSize, promotionsListHead, type SeoHead } from '@/seo/templates';
import { DEFAULT_LOCALE, type Locale } from '@/seo/locales';
import { LOCALE_ROUTES_LIVE, localePath, splitLocalePath, type EntityRef, type EntityType } from '@/seo/urls';

/** What the server decided for one document request (server/routes.mjs `decide`, plus the 503). */
export interface DocumentDecision {
  status: number;
  /** Set for 301 / 302: the one redirect target, query kept. */
  location?: string;
  /** Set for 200 / 404 / 410. */
  head?: SeoHead;
  /** Set for 503: seconds for `Retry-After`. */
  retryAfter?: number;
}

/** A served entity's detail-query data (`brandById`, …), as the server's head was built from it. */
export interface DecidedEntity {
  type: EntityType;
  item: EntityRef & Record<string, unknown>;
}

/** `DocumentDecision` for an entity route (web plan W4.4): plus the entity on a served page. */
export interface EntityDecision extends DocumentDecision {
  /** Set for 200 when the URL serves an entity of the asked type. */
  entity?: DecidedEntity;
  /**
   * With `entity`: the answers of the page's secondary queries (the brand
   * hub, the promotion rails) by `preloadKey`; a failed one is left out.
   */
  preload?: RoutePreloadData;
}

/** `DocumentDecision` for a home, list or hub route (web plan W4.5): plus its page data on a served page. */
export interface PageDecision extends DocumentDecision {
  /** Set for 200: the answers of the page's queries, by `preloadKey` (@/seo/routePreload). */
  preload?: RoutePreloadData;
}

/** The SEO server's part of the load context (server/framework.mjs). */
export interface SeoDocumentContext {
  /** The decision for this request's URL (raw path, query, visitor IP already bound). */
  resolve(): Promise<DocumentDecision>;
  /**
   * The same decision (one per request, shared with `resolve()`), with the
   * GraphQL page data of the entity this URL serves when it is of `type`.
   * The key, locale and canonical check are the URL's, exactly as the root
   * loader's answer read them (web plan W4.4).
   */
  entity(type: EntityType): Promise<EntityDecision>;
  /**
   * The same decision, with the data the page's queries would load (web plan
   * W4.5: home, the lists, the hubs, the sale pages, /nearby), read with the
   * exact documents and variables the page sends.
   */
  page(): Promise<PageDecision>;
}

declare module 'react-router' {
  interface AppLoadContext {
    /** Absent without the SEO server (`react-router dev`). */
    seo?: SeoDocumentContext;
    /** Reports an unexpected server error (W1.5 error tracking), scrubbed. */
    reportError?: (error: unknown, request: Request) => void;
  }
}

/** The root loader's data. */
export interface DocumentData {
  /** The server's head for this URL; null without the SEO server. */
  head: SeoHead | null;
  /** A 4xx answer: no canonical, no `og:url` (an error page has no address of its own). */
  error: boolean;
  /**
   * `pathname + search` of the URL the document was served for: the head is
   * that URL's, and only that URL's (`servesDocumentHead`). Absent when the
   * loader ran without its request (tests).
   */
  path?: string;
  /**
   * When the server rendered the document (ms since the epoch): the clock the
   * page reads on the server and while it hydrates (@/hooks/useRenderNow).
   */
  renderedAt?: number;
}

/**
 * Whether the server's head in `root` is the head of `location`: the
 * document's own URL, before any in-app navigation moved away from it (or
 * after one came back to it). There the root's and the route modules' `meta`
 * render it and the page renders none of its own (@/components/seo/PageHead);
 * anywhere else, and without the SEO server, the page's own head is the head.
 * No location (outside a router) counts as the document's.
 */
export function servesDocumentHead(
  root: DocumentData | null | undefined,
  location: { pathname: string; search: string } | null | undefined,
): root is DocumentData & { head: SeoHead } {
  if (!root?.head) return false;
  if (!location || root.path === undefined) return true;
  return root.path === `${location.pathname}${location.search}`;
}

export const CACHE_CONTROL = Object.freeze({
  ok: 'public, max-age=0, s-maxage=60, stale-while-revalidate=600',
  redirect: 'public, max-age=0, s-maxage=60',
  clientError: 'no-cache',
  serverError: 'no-store',
});

/** W1.3's Cache-Control for a status (server/routes.mjs `cacheControlFor`). */
export function documentCacheControl(status: number): string {
  if (status >= 500) return CACHE_CONTROL.serverError;
  if (status >= 400) return CACHE_CONTROL.clientError;
  if (status >= 300) return CACHE_CONTROL.redirect;
  return CACHE_CONTROL.ok;
}

/**
 * The root loader: the server's decision as React Router's answer (see the
 * file comment). Throws the redirect and the 503; anything else the server
 * throws (a bug) propagates as a 500.
 */
export async function loadDocument(
  context: AppLoadContext | undefined | null,
  request?: Request,
): Promise<DocumentData | DataWithResponseInit<DocumentData>> {
  const seo = context?.seo;
  if (!seo) return { head: null, error: false, renderedAt: Date.now() };
  const url = request ? new URL(request.url) : null;
  const path = url ? { path: `${url.pathname}${url.search}` } : {};
  const decision = await seo.resolve();
  const headers = { 'Cache-Control': documentCacheControl(decision.status) };
  if (decision.location) {
    throw redirect(decision.location, { status: decision.status, headers });
  }
  if (decision.status >= 500 || !decision.head) {
    const status = decision.status >= 500 ? decision.status : 500;
    throw data(null, {
      status,
      headers: {
        'Cache-Control': documentCacheControl(status),
        ...(decision.retryAfter ? { 'Retry-After': String(decision.retryAfter) } : {}),
      },
    });
  }
  // Returned, not thrown: the status without the error boundary.
  return data<DocumentData>(
    { head: decision.head, error: decision.status >= 400, ...path, renderedAt: Date.now() },
    { status: decision.status, headers },
  );
}

const property = (key: string, content: string): MetaDescriptor => ({ property: key, content });
const named = (key: string, content: string): MetaDescriptor => ({ name: key, content });

/**
 * `head` as `meta` descriptors: the tags server/head.mjs `renderHeadTags`
 * writes, in its order, minus the JSON-LD (`jsonLdHtml`, rendered in the
 * body). The robots and the canonical carry `data-seo-server`, as the W1
 * server's did (@/lib/serverHead): the raw head stays byte-for-byte W1's.
 * `error` leaves out the canonical and `og:url`.
 */
export function headMeta(head: SeoHead, { error = false } = {}): MetaDescriptor[] {
  const mark = { [SERVER_HEAD_ATTRIBUTE]: '' };
  const tags: MetaDescriptor[] = [
    { title: head.title },
    named('description', head.description),
    { name: 'robots', content: head.robots, ...mark },
  ];
  if (!error) tags.push({ tagName: 'link', rel: 'canonical', href: head.canonical, ...mark });
  if (!error) {
    for (const alternate of head.alternates ?? []) {
      tags.push({ tagName: 'link', rel: 'alternate', hreflang: alternate.hreflang, href: alternate.url });
    }
  }
  tags.push(
    property('og:type', head.og.type),
    property('og:site_name', head.og.siteName),
    property('og:locale', head.og.locale),
    property('og:title', head.og.title),
    property('og:description', head.og.description),
  );
  if (!error) tags.push(property('og:url', head.og.url));
  tags.push(property('og:image', head.og.image));
  const size = ogImageSize(head.og.image);
  if (size) {
    tags.push(property('og:image:width', String(size.width)), property('og:image:height', String(size.height)));
  }
  tags.push(
    property('og:image:alt', head.og.imageAlt),
    named('twitter:card', head.twitter.card),
    named('twitter:title', head.twitter.title),
    named('twitter:description', head.twitter.description),
    named('twitter:image', head.twitter.image),
    named('twitter:image:alt', head.twitter.imageAlt),
  );
  return tags;
}

/** JSON for inside `<script>`: no `</script>`, `<!--` or line separators can break out (server/head.mjs `scriptJson`). */
export function scriptJson(value: unknown): string {
  return JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

/** The document's one JSON-LD script: the root Layout's on the served URL, a page's own (`<PageHead/>`) elsewhere. */
export const JSONLD_ELEMENT_ID = 'seo-jsonld';

/** The head's JSON-LD graph as script text, or null when it has none. */
export function jsonLdHtml(head: SeoHead | null): string | null {
  if (!head?.jsonLd?.length) return null;
  return scriptJson({ '@context': 'https://schema.org', '@graph': head.jsonLd });
}

/** A catalog link where it lives in `locale` (after the URL move, `/promotions` → `/bh/en/promotions`). */
function catalogHref(path: string, live: boolean, locale: Locale): string {
  const route = LEGACY_ROUTES.find((candidate) => candidate.catalog && candidate.path === path);
  if (!route || !live) return path;
  return locale === DEFAULT_LOCALE ? (movedPathFor(route, live) ?? path) : localePath(locale, path);
}

/**
 * What crawlers and browsers without JavaScript read in the `<noscript>`
 * block (index.html's, which server/head.mjs filled per page): the page's H1
 * and description, and links to the two list pages under their H1s.
 */
export function noscriptCopy(
  head: SeoHead | null,
  {
    live = LOCALE_ROUTES_LIVE,
    locale = DEFAULT_LOCALE,
    t = tFor(locale),
  }: { live?: boolean; locale?: Locale; t?: Translate } = {},
) {
  const home = homeHead({ liveOffers: false }, { locale, t });
  return {
    h1: (head ?? home).h1,
    description: (head ?? home).description,
    intro: t('common:document.noscript'),
    links: [
      { href: catalogHref('/promotions', live, locale), text: promotionsListHead({}, { locale, t }).h1 },
      { href: catalogHref('/brands', live, locale), text: brandsListHead({}, { locale, t }).h1 },
    ],
  };
}
